/**
 * ProductGrid — 8-product suite grid per Home v1 spec §3.5.
 *
 * One card per product. Featured cards carry the reserved 6px coral
 * left-border pattern per tokens.md §7.1 — used for high-conviction stops
 * in the grid (Career Transition orchestrator + AI Interview Coach proven).
 *
 * Responsive: 3-col ≥1101px, 2-col 901–1100px, 1-col ≤900px.
 */

import React from "react";
import { Box, Typography, alpha, useTheme } from "@mui/material";
import type { Theme } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { HOME_PRODUCTS, HomeProduct, HomeProductChip } from "../../data/homeProducts";
import { L } from "../../utils/localized";
import BoldLede from "./BoldLede";
import { standaloneChipInk } from "../../theme/tokens";

function chipStyle(chip: HomeProductChip, theme: Theme, t: TFunction) {
  switch (chip) {
    case "live":
      return {
        bg: alpha(theme.palette.success.main, 0.1),
        color: theme.palette.success.main,
        label: t("ProductGrid.chipLive"),
      };
    case "soon":
      return {
        bg: alpha(theme.palette.warning.main, 0.1),
        color: theme.palette.warning.main,
        label: t("ProductGrid.chipSoon"),
      };
    case "hero":
      return {
        bg: alpha(theme.palette.secondary.main, 0.08),
        color: theme.palette.secondary.main,
        label: t("ProductGrid.chipYourPlan"),
      };
    case "free":
      return {
        bg: alpha(theme.palette.success.main, 0.1),
        color: theme.palette.success.main,
        label: t("ProductGrid.chipFree"),
      };
    case "proven":
      return {
        bg: alpha(theme.palette.secondary.main, 0.1),
        color: theme.palette.secondary.main,
        label: t("ProductGrid.chipProven"),
      };
    case "standalone":
      // Deliberately NOT a success/green chip. Every other chip here describes
      // availability WITHIN the plan ("Live", "Your plan", "Free"). A one-off
      // purchase listed in that vocabulary reads as included, which is how you
      // generate refund requests. Warning-toned so it reads as "different terms".
      // Ink comes from a token, NOT theme.palette.warning.dark — that value is
      // mode-invariant and fails AA on this chip in both themes. See tokens.ts.
      return {
        bg: alpha(theme.palette.warning.main, 0.14),
        color: standaloneChipInk(theme.palette.mode),
        label: t("ProductGrid.chipStandalone"),
      };
  }
}

function ProductCard({ product }: { product: HomeProduct }) {
  const theme = useTheme();
  const { t } = useTranslation("home");
  const chip = chipStyle(product.chip, theme, t);

  return (
    <Box
      component={RouterLink}
      to={product.href}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        minHeight: 220,
        p: 2.5,
        borderRadius: "12px",
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        color: "inherit",
        textDecoration: "none",
        position: "relative",
        transition: theme.transitions.create(
          ["border-color", "box-shadow", "transform"],
          { duration: theme.transitions.duration.shortest },
        ),
        ...(product.featured && {
          borderLeftWidth: "6px",
          borderLeftColor: "secondary.main",
          pl: "calc(20px - 5px)",
        }),
        "&:hover": {
          borderColor: "text.secondary",
          boxShadow:
            "0 4px 6px -1px rgba(0,0,0,0.07), 0 2px 4px -2px rgba(0,0,0,0.05)",
          transform: "translateY(-1px)",
          ...(product.featured && { borderLeftColor: "secondary.main" }),
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Typography
          component="span"
          sx={{
            fontFamily: '"JetBrains Mono", ui-monospace, monospace',
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: "0.04em",
            color: "text.secondary",
          }}
        >
          {product.num}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          · {product.role}
        </Typography>
        <Box
          sx={{
            ml: "auto",
            px: 1,
            py: 0.25,
            borderRadius: "6px",
            bgcolor: chip.bg,
            color: chip.color,
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {chip.label}
        </Box>
      </Box>

      <Typography
        component="h3"
        sx={{
          fontSize: 17,
          fontWeight: 500,
          lineHeight: 1.25,
          letterSpacing: "-0.01em",
          color: "text.primary",
          m: 0,
        }}
      >
        {product.title}
      </Typography>

      <BoldLede
        text={product.lede}
        sx={{
          display: "block",
          fontSize: 13.5,
          lineHeight: 1.5,
          color: "text.secondary",
        }}
      />

      <Box
        component="ul"
        sx={{
          m: 0,
          p: 0,
          pl: 2.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          listStyle: "disc",
          flex: 1,
        }}
      >
        {product.features.map((f, i) => (
          <Typography
            component="li"
            key={i}
            sx={{ fontSize: 12.5, color: "text.secondary" }}
          >
            {f}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          mt: "auto",
          pt: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "secondary.main",
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        {product.routeLabel}
        <ArrowForwardIcon sx={{ fontSize: 14 }} />
      </Box>
    </Box>
  );
}

export default function ProductGrid() {
  const { t, i18n } = useTranslation("home");
  const homeProducts = L(HOME_PRODUCTS, i18n.language);
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: 24,
            fontWeight: 500,
            letterSpacing: "-0.02em",
            color: "text.primary",
            m: 0,
          }}
        >
          {t("ProductGrid.title")}
        </Typography>
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          {t("ProductGrid.productCount", { count: homeProducts.length })}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
        }}
      >
        {homeProducts.map((p) => (
          <ProductCard key={p.href} product={p} />
        ))}
      </Box>
    </Box>
  );
}
